import classNames from 'classnames';
import { type ButtonHTMLAttributes, type FC, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Icon } from '../Icon/Icon';
import './ColorPicker.css';
import { getAccessibleTabIndex } from '../Button/ariaHelpers';
import { useStableCallback } from '../hooks/useStableCallback';
import { swatches } from './ColorPicker.constants';

export type ColorPickerProps = {
  // color value in hex format controlling the color picker's state
  color: string;
  // indicates whether the color picker should be initially expanded; further state is controlled by the component
  initiallyExpanded?: boolean;
  // callback function invoked when the color picker's state changes
  onColorChange: (color: string) => void;
};

export const ColorPicker: FC<ColorPickerProps> = (props) => {
  const [expanded, setExpanded] = useState(props.initiallyExpanded || false);

  const toggleExpanded = useStableCallback(() => {
    setExpanded((prevState) => !prevState);
  });

  return (
    <div>
      <div className='p'>
        <div className='text-sm text-default font-semibold mb-sm'>
          Choose a color
        </div>
        <div className='grid gap-3xs grid-rows-auto grid-cols-9'>
          {swatches.map((row) =>
            row.map((color) => (
              <Swatch
                key={color}
                color={color}
                onClick={() => props.onColorChange(color)}
              />
            )),
          )}
        </div>
      </div>
      <div className='border-t border-t-divider p'>
        <button
          type='button'
          className='flex w-full items-center justify-between'
          tabIndex={0}
          onClick={toggleExpanded}
        >
          <div className='flex items-center gap-x-2xs'>
            <Icon glyph='fa-palette' size='sm' />
            <div className='text-sm text-default font-semibold'>
              More colors
            </div>
          </div>
          <Icon
            glyph={expanded ? 'fa-angle-up' : 'fa-angle-down'}
            size='sm'
            className='text-element-secondary'
          />
        </button>
        {expanded && (
          <div>
            <div className='color-picker mt-sm'>
              <HexColorPicker
                color={props.color}
                onChange={props.onColorChange}
              />
            </div>
            <div className='flex gap-x-xs mt-xs w-full items-center'>
              <span className='text-default text-sm'>HEX</span>
              <HexColorInput
                color={props.color}
                onChange={props.onColorChange}
                className='text-default border border-default bg-default p-2xs rounded text-xs grow'
                prefixed
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type SwatchProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  color: string;
};

const Swatch: FC<SwatchProps> = ({
  color,
  className,
  onClick,
  ...restProps
}) => {
  return (
    <button
      type='button'
      className={classNames(
        'border border-shade-15 size-5 rounded hover:bg-state-hover',
        className,
      )}
      style={{
        backgroundColor: color,
      }}
      onClick={onClick}
      tabIndex={getAccessibleTabIndex(restProps.disabled)}
      {...restProps}
    />
  );
};
